import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  Typography,
  Tooltip,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Divider,
  Container,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  DataGrid,
  GridToolbar
} from "@mui/x-data-grid";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate } from 'react-router-dom';
import {
  EditOutlined,
  Visibility,
  VisibilityOff,
  DeleteOutline,
  CheckCircle,
  PriorityHigh,
  AccessTime,
  Add,
  AccountBalance,
  TrendingUp,
  ReceiptLong,
  MonetizationOn,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import DeleteWithPassword from "../components/deleteWithPassword";
import RoleGuard from "../components/RoleGuard";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ResponsiveAppBar from "../components/AppBar";
import { API_BASE_URL } from "../api/config";
import CustomerSearchBar, { matchCustomerSearch } from "../components/CustomerSearchBar";

dayjs.extend(customParseFormat);

const BASE_URL = `${API_BASE_URL}/velavan_finance`;

// Create theme with specified color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' },
    secondary: { main: '#936097' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: { main: '#77c8c7' },
    info: { main: '#936097' },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.05rem',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          boxShadow: '0 4px 12px rgba(147, 96, 151, 0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(119, 200, 199, 0.25)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});

const DueMatters = () => {
  const [entries, setEntries] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingSingleDue, setEditingSingleDue] = useState(null);
  const [singleDueIndex, setSingleDueIndex] = useState(null);
  const [openSingleDueDialog, setOpenSingleDueDialog] = useState(false);
  const [selectedPage, setSelectedPage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/get_all_entries`);
      const entriesWithId = res.data.map(entry => ({
        ...entry,
        id: entry._id,
      }));
      setEntries(entriesWithId);
    } catch (error) {
      console.error("Error fetching entries:", error);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Filtered Entries based on search query
  const filteredEntries = entries.filter((entry) => matchCustomerSearch(entry, searchQuery));

  // Summary Metrics
  const totalLended = filteredEntries.reduce((acc, curr) => acc + (parseFloat(curr.lendedAmount) || 0), 0);
  const totalPrincipal = filteredEntries.reduce((acc, curr) => acc + (parseFloat(curr.totalPrincipalDue) || 0), 0);
  const totalInterest = filteredEntries.reduce((acc, curr) => acc + (parseFloat(curr.totalInterestDue) || 0), 0);
  const totalPaid = filteredEntries.reduce((acc, curr) => acc + (parseFloat(curr.paidDueAmount) || 0), 0);

  const columns = [
    {
      field: "expand",
      headerName: "",
      width: 90,
      sortable: false,
      renderHeader: () => (
        <Tooltip title="Edit selected entry">
          <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
            <IconButton
              onClick={() => {
                if (!selectedRowId) {
                  alert("Please select a row to edit.");
                  return;
                }
                const selectedEntry = entries.find((entry) => entry.id === selectedRowId);
                if (selectedEntry) {
                  setEditingEntry({ ...selectedEntry });
                  setOpenEditDialog(true);
                }
              }}
              size="small"
              sx={{ color: '#77c8c7' }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </RoleGuard>
        </Tooltip>
      ),
      renderCell: (params) => (
        <Tooltip title={expandedRowId === params.id ? "Hide dues timeline" : "Inspect dues breakdown"}>
          <IconButton
            onClick={() => {
              setExpandedRowId(expandedRowId === params.id ? null : params.id);
            }}
            size="small"
            sx={{
              color: expandedRowId === params.id ? '#936097' : '#77c8c7',
              bgcolor: expandedRowId === params.id ? 'rgba(147, 96, 151, 0.1)' : 'rgba(119, 200, 199, 0.1)',
            }}
          >
            {expandedRowId === params.id ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "refNo",
      headerName: "Ref No",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#0f766e', fontWeight: 700, borderRadius: 1.5 }}
        />
      )
    },
    {
      field: "name",
      headerName: "Customer Name",
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          {params.value}
        </Typography>
      )
    },
    { field: "date", headerName: "Date", width: 120 },
    {
      field: "lendedAmount",
      headerName: "Lended Capital",
      width: 150,
      type: "number",
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: "weeklyDue",
      headerName: "Weekly Due",
      width: 130,
      type: "number",
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#936097', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: "totalDues",
      headerName: "Total Dues",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 700 }} />
      )
    },
    {
      field: "duesPaid",
      headerName: "Dues Paid",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" sx={{ bgcolor: 'rgba(34, 197, 94, 0.15)', color: '#16a34a', fontWeight: 700 }} />
      )
    },
    {
      field: "remainingDues",
      headerName: "Remaining",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#b45309', fontWeight: 700 }} />
      )
    },
    {
      field: "pendingDue",
      headerName: "Pending Due",
      width: 130,
      renderCell: (params) => {
        const value = params.value !== undefined && params.value !== null ? params.value : 0;
        return (
          <Chip
            label={value > 0 ? `₹${value.toLocaleString()}` : "Clear"}
            size="small"
            sx={{
              bgcolor: value > 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
              color: value > 0 ? '#b91c1c' : '#15803d',
              fontWeight: 700,
              borderRadius: 2,
            }}
          />
        );
      },
    },
    {
      field: "accountStatus",
      headerName: "Account Status",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const status = params.value?.toLowerCase() || "active";
        let bg = "rgba(119, 200, 199, 0.15)";
        let text = "#0f766e";
        if (status === "closed/paid" || status === "closed" || status === "paid") {
          bg = "rgba(254, 226, 226, 0.9)";
          text = "#991b1b";
        } else if (status === "npa") {
          bg = "rgba(245, 158, 11, 0.15)";
          text = "#b45309";
        }
        return (
          <Chip
            label={status.toUpperCase()}
            size="small"
            sx={{
              bgcolor: bg,
              color: text,
              fontWeight: 700,
              borderRadius: 2,
            }}
          />
        );
      },
    },
  ];

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${BASE_URL}/update_by_ref/${editingEntry.refNo}`, editingEntry);
      setOpenEditDialog(false);
      fetchEntries();
    } catch (error) {
      console.error("Error updating entry:", error);
    }
  };

  const handleDueChange = (index, field, value) => {
    const updatedDues = [...editingEntry.dues];
    updatedDues[index][field] = field === "principalAmount" || field === "interestAmount" ? parseFloat(value) || 0 : value;
    setEditingEntry({ ...editingEntry, dues: updatedDues });
  };

  const handleAddDue = () => {
    const newDue = {
      target: dayjs().format("DD-MM-YYYY"),
      actual: "",
      principalAmount: 0,
      interestAmount: 0
    };
    setEditingEntry({
      ...editingEntry,
      dues: [...editingEntry.dues, newDue]
    });
  };

  const handleRemoveDue = (index) => {
    const updatedDues = [...editingEntry.dues];
    updatedDues.splice(index, 1);
    setEditingEntry({ ...editingEntry, dues: updatedDues });
  };

  const handleSingleDueClick = (entry, dueIndex) => {
    if (entry && entry.dues && entry.dues[dueIndex]) {
      setSingleDueIndex(dueIndex);
      setEditingSingleDue({ ...entry.dues[dueIndex] });
      setEditingEntry({ ...entry });
      setOpenSingleDueDialog(true);
    }
  };

  const handleSingleDueSubmit = async () => {
    try {
      const updatedEntry = { ...editingEntry };
      updatedEntry.dues[singleDueIndex] = editingSingleDue;

      await axios.put(`${BASE_URL}/update_by_ref/${updatedEntry.refNo}`, updatedEntry);
      setOpenSingleDueDialog(false);
      fetchEntries();
    } catch (error) {
      console.error("Error updating due:", error);
    }
  };

  const handleSingleDueChange = (field, value) => {
    setEditingSingleDue({
      ...editingSingleDue,
      [field]: field === "principalAmount" || field === "interestAmount" ? parseFloat(value) || 0 : value
    });
  };

  const getDueStatus = (due) => {
    if (!due) {
      return {
        color: "#64748b",
        bgColor: "#f8fafc",
        borderColor: "rgba(226, 232, 240, 0.9)",
        icon: <AccessTime fontSize="small" sx={{ color: '#94a3b8' }} />,
        text: "Unknown"
      };
    }

    const today = dayjs();
    const dueDate = dayjs(due.target, "DD-MM-YYYY", true);
    const isPaid = due.actual && (due.actual || "").trim() !== "";

    if (isPaid) {
      return {
        color: "#16a34a",
        bgColor: "rgba(34, 197, 94, 0.08)",
        borderColor: "rgba(34, 197, 94, 0.3)",
        icon: <CheckCircle fontSize="small" sx={{ color: '#22c55e' }} />,
        text: "Paid"
      };
    } else if (dueDate.isValid() && dueDate.isBefore(today, "day")) {
      return {
        color: "#dc2626",
        bgColor: "rgba(239, 68, 68, 0.08)",
        borderColor: "rgba(239, 68, 68, 0.3)",
        icon: <PriorityHigh fontSize="small" sx={{ color: '#ef4444' }} />,
        text: "Overdue"
      };
    } else {
      return {
        color: "#d97706",
        bgColor: "rgba(245, 158, 11, 0.08)",
        borderColor: "rgba(245, 158, 11, 0.3)",
        icon: <AccessTime fontSize="small" sx={{ color: '#f59e0b' }} />,
        text: "Upcoming"
      };
    }
  };

  const expandedEntry = entries.find((e) => e.id === expandedRowId);

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="xl">
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, borderRadius: '50px', bgcolor: 'rgba(119, 200, 199, 0.15)', mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Due Operations & Collections
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Due Matters & Timeline Tracker
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Monitor scheduled installment collections, overdue flags & individual loan schedules
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                <Select
                  value={selectedPage}
                  label="Navigate Module"
                  onChange={handleNavigate}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#77c8c7' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#936097' },
                  }}
                >
                  <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                  <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                  <MenuItem value="/due-matters">Due Matters</MenuItem>
                  <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                  <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                  <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                  <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Top KPI Metrics */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(119, 200, 199, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MonetizationOn sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Capital Lended
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        ₹{totalLended.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(147, 96, 151, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TrendingUp sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Principal in Dues
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        ₹{totalPrincipal.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(245, 158, 11, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ReceiptLong sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Interest in Dues
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#b45309' }}>
                        ₹{totalInterest.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(34, 197, 94, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Collected Amount
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d' }}>
                        ₹{totalPaid.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Customer Search Card */}
            <CustomerSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              totalCount={entries.length}
              filteredCount={filteredEntries.length}
              placeholder="Search by Refer ID (e.g. VF-0001), Customer Name, Contact Number, or Aadhaar No..."
              title="Due Matters Customer Search"
              subtitle="Quickly search dues and installment breakdowns across Refer ID, Name, Phone & Aadhaar"
            />

            {/* Main Overview DataGrid */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
                p: 2.5,
                mb: 4,
              }}
            >
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccountBalance />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Active Due Installments Directory
                  </Typography>
                </Box>
                <Chip
                  label={`${filteredEntries.length} of ${entries.length} Customers`}
                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, borderRadius: 2 }}
                />
              </Box>

              <Box sx={{ height: 520, width: "100%" }}>
                <DataGrid
                  rows={filteredEntries}
                  columns={columns}
                  pagination
                  checkboxSelection
                  components={{ Toolbar: GridToolbar }}
                  onRowSelectionModelChange={(newSelection) => {
                    setSelectedRowId(newSelection[0] || null);
                  }}
                  getRowId={(row) => row._id}
                  getRowClassName={(params) => {
                    const status = params.row.accountStatus?.toLowerCase();
                    if (status === "closed/paid" || status === "closed" || status === "paid") {
                      return "closed-row";
                    }
                    if (status === "npa") {
                      return "npa-row";
                    }
                    return "active-row";
                  }}
                  sx={{
                    border: 'none',
                    fontSize: { xs: "12px", md: "13.5px" },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f8fafc',
                      color: '#475569',
                      fontWeight: 700,
                    },
                    '& .closed-row': {
                      backgroundColor: 'rgba(254, 242, 242, 0.85) !important',
                      color: '#991b1b',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'rgba(254, 226, 226, 0.95) !important',
                      },
                    },
                    '& .npa-row': {
                      backgroundColor: 'rgba(254, 243, 199, 0.45) !important',
                      color: '#b45309',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(254, 243, 199, 0.7) !important',
                      },
                    },
                    '& .active-row': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(119, 200, 199, 0.08) !important',
                      },
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s',
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Collapsible Customer Dues Breakdown */}
            {expandedRowId !== null && expandedEntry && (
              <Collapse in={true} timeout="auto" unmountOnExit>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3.5,
                    mb: 4,
                    borderRadius: 4,
                    bgcolor: '#ffffff',
                    border: '1px solid rgba(119, 200, 199, 0.4)',
                    boxShadow: '0 12px 32px rgba(119, 200, 199, 0.12)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Chip
                          label={expandedEntry.refNo}
                          sx={{ bgcolor: 'rgba(119, 200, 199, 0.2)', color: '#0f766e', fontWeight: 800, borderRadius: 2 }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                          {expandedEntry.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Showing schedule of {expandedEntry.dues?.length || 0} scheduled installments
                      </Typography>
                    </Box>

                    {/* Status Legend Badges */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        icon={<CheckCircle sx={{ color: '#22c55e !important' }} />}
                        label="Paid"
                        size="small"
                        sx={{ bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#16a34a', fontWeight: 700 }}
                      />
                      <Chip
                        icon={<PriorityHigh sx={{ color: '#ef4444 !important' }} />}
                        label="Overdue"
                        size="small"
                        sx={{ bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', fontWeight: 700 }}
                      />
                      <Chip
                        icon={<AccessTime sx={{ color: '#f59e0b !important' }} />}
                        label="Upcoming"
                        size="small"
                        sx={{ bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#d97706', fontWeight: 700 }}
                      />
                    </Box>
                  </Box>

                  {/* Customer Quick Financial Cards */}
                  <Grid container spacing={2} sx={{ mb: 3.5 }}>
                    {[
                      { label: 'Lended Capital', value: expandedEntry.lendedAmount, color: '#0f766e', bg: 'rgba(119, 200, 199, 0.15)' },
                      { label: 'Total Principal', value: expandedEntry.totalPrincipalDue, color: '#936097', bg: 'rgba(147, 96, 151, 0.12)' },
                      { label: 'Total Interest', value: expandedEntry.totalInterestDue, color: '#b45309', bg: 'rgba(245, 158, 11, 0.12)' },
                      { label: 'Total Paid', value: expandedEntry.paidDueAmount, color: '#15803d', bg: 'rgba(34, 197, 94, 0.12)' },
                    ].map((item, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Box sx={{ p: 2, borderRadius: 3, bgcolor: item.bg, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: item.color }}>
                            ₹{item.value?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ mb: 3 }} />

                  {/* Dues Cards Grid */}
                  <Grid container spacing={2}>
                    {expandedEntry.dues?.map((due, i) => {
                      const status = getDueStatus(due);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5,
                              backgroundColor: status.bgColor,
                              border: `1px solid ${status.borderColor}`,
                              borderRadius: 3,
                              transition: 'all 0.25s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
                              },
                            }}
                            onClick={() => handleSingleDueClick(expandedEntry, i)}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                              <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
                                Due #{i + 1}
                              </Typography>
                              <Chip
                                size="small"
                                label={status.text}
                                icon={status.icon}
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: '#ffffff',
                                  color: status.color,
                                  borderColor: status.borderColor,
                                }}
                              />
                            </Box>
                            <Divider sx={{ mb: 1.5, borderColor: 'rgba(0,0,0,0.04)' }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                                🎯 Target Date: <strong style={{ color: '#1e293b' }}>{due?.target || "--"}</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                                ✅ Actual Date: <strong style={{ color: due?.actual ? '#15803d' : '#94a3b8' }}>{due?.actual || "Pending"}</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                                💸 Principal: <strong style={{ color: '#1e293b' }}>₹{due?.principalAmount?.toLocaleString() || "0"}</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                                📈 Interest: <strong style={{ color: '#936097' }}>₹{due?.interestAmount?.toLocaleString() || "0"}</strong>
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Collapse>
            )}

            {/* Edit Full Entry Dialog */}
            <Dialog
              open={openEditDialog}
              onClose={() => setOpenEditDialog(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
            >
              <DialogTitle sx={{ background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)', color: '#ffffff', fontWeight: 700 }}>
                Edit Schedule - {editingEntry?.refNo} ({editingEntry?.name})
              </DialogTitle>
              <DialogContent sx={{ pt: 3, bgcolor: '#ffffff', maxHeight: 500 }}>
                {editingEntry?.dues?.map((due, index) => {
                  const status = getDueStatus(due);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1.5,
                        alignItems: { xs: 'stretch', sm: 'center' },
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2.5,
                        borderLeft: `4px solid ${status.color}`,
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Badge
                        color={status.text === "Paid" ? "success" : status.text === "Overdue" ? "error" : "warning"}
                        badgeContent={index + 1}
                        sx={{ mr: 1 }}
                      >
                        {status.icon}
                      </Badge>
                      <TextField
                        label="Target Date"
                        value={due?.target || ""}
                        onChange={(e) => handleDueChange(index, "target", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Actual Date"
                        value={due?.actual || ""}
                        onChange={(e) => handleDueChange(index, "actual", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Principal (₹)"
                        type="number"
                        value={due?.principalAmount || 0}
                        onChange={(e) => handleDueChange(index, "principalAmount", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Interest (₹)"
                        type="number"
                        value={due?.interestAmount || 0}
                        onChange={(e) => handleDueChange(index, "interestAmount", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <RoleGuard allowedRoles={["Admin Manager"]}>
                        <Tooltip title="Remove due row">
                          <DeleteWithPassword
                            resourceId={`due-${index}`}
                            resourceName="Due"
                            onDelete={async () => {
                              try {
                                await handleRemoveDue(index);
                              } catch (error) {
                                console.error("Error removing due:", error);
                                throw error;
                              }
                            }}
                            navigate={navigate}
                            renderTrigger={(onClick) => (
                              <IconButton onClick={onClick} color="error" size="small">
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            )}
                          />
                        </Tooltip>
                      </RoleGuard>
                    </Box>
                  );
                })}

                <Button
                  onClick={handleAddDue}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1, py: 1.2, borderColor: '#77c8c7', color: '#0f766e', borderRadius: 2.5 }}
                  startIcon={<Add />}
                >
                  Add Next Installment Due Row
                </Button>
              </DialogContent>

              <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleEditSubmit}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                >
                  Save Schedule Changes
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Single Due Dialog */}
            <Dialog
              open={openSingleDueDialog}
              onClose={() => setOpenSingleDueDialog(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
            >
              <DialogTitle sx={{ background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)', color: '#ffffff', fontWeight: 700 }}>
                Update Installment #{singleDueIndex !== null ? singleDueIndex + 1 : ''} • {editingEntry?.refNo}
              </DialogTitle>
              <DialogContent sx={{ pt: 3, bgcolor: '#ffffff' }}>
                {editingSingleDue && (
                  <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
                    <TextField
                      label="Target Date (DD-MM-YYYY)"
                      value={editingSingleDue.target || ""}
                      onChange={(e) => handleSingleDueChange("target", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Actual Payment Date (Leave empty if unpaid)"
                      value={editingSingleDue.actual || ""}
                      onChange={(e) => handleSingleDueChange("actual", e.target.value)}
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      helperText="Entering a date marks this installment as Paid."
                    />
                    <TextField
                      label="Principal Amount (₹)"
                      type="number"
                      value={editingSingleDue.principalAmount || 0}
                      onChange={(e) => handleSingleDueChange("principalAmount", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Interest Amount (₹)"
                      type="number"
                      value={editingSingleDue.interestAmount || 0}
                      onChange={(e) => handleSingleDueChange("interestAmount", e.target.value)}
                      fullWidth
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setOpenSingleDueDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSingleDueSubmit}
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                >
                  Save Installment Status
                </Button>
              </DialogActions>
            </Dialog>
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default DueMatters;